// parseAdjustmentFill.ts
//
// Decodes PSD Additional Layer Information blocks for:
//   - Adjustment layers (brit, levl, curv, hue2/hue , blnc, blwh, phfl, mixr,
//     nvrt, post, thrs, grdm, selc, vibA, expA, AdjL)
//   - Fill layers (SoCo solid color, GdFl gradient fill, PtFl pattern fill)
//
// All blocks use the OSType descriptor format (versioned: 4-byte version + descriptor).
// Color components in PSD descriptors are stored as 0..255 doubles.
//
// IMPORTANT: no DOM, no Buffer — core/ only.

import { BinaryReader, BinaryReaderError } from "./BinaryReader";
import {
  OSTypeDescriptorError,
  readVersionedDescriptor,
  readDescriptor,
  asDescriptor,
  asDouble,
  asBoolean,
  asInteger,
  asString,
  asList,
  asEnumerated,
  getItem,
  type OSTypeValue,
  type OSTypeItems,
} from "./osTypeDescriptor";
import type { AdjustmentKind } from "../../document/Layer";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ParsedAdjustment {
  kind: AdjustmentKind;
  params: Record<string, number | string | boolean>;
}

export interface RgbColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface ParsedSolidFill {
  color: RgbColor;
}

export interface GradientStop {
  position: number; // 0..1
  color: RgbColor;
  midpoint: number; // 0..1
}

export interface ParsedGradientFill {
  gradientType: "linear" | "radial" | "angle" | "reflected" | "diamond";
  angle: number;
  scale: number;
  reverse: boolean;
  stops: GradientStop[];
}

export interface ParsedPatternFill {
  patternName: string;
  scale: number;
}

// ---------------------------------------------------------------------------
// Adjustment layer — detect kind + decode params
// ---------------------------------------------------------------------------

/** Map from PSD additionalInfo key to our internal AdjustmentKind. */
const ADJ_KEY_TO_KIND: Record<string, AdjustmentKind> = {
  brit: "brightness-contrast",
  levl: "levels",
  curv: "curves",
  hue2: "hue-saturation",
  "hue ": "hue-saturation",
  blnc: "color-balance",
  blwh: "black-white",
  phfl: "photo-filter",
  PhFl: "photo-filter",
  mixr: "channel-mixer",
  nvrt: "invert",
  post: "posterize",
  thrs: "threshold",
  grdm: "gradient-map",
  selc: "selective-color",
  vibA: "vibrance",
  expA: "exposure",
};

/**
 * Given a layer's additionalInfo array, detect which adjustment kind it is
 * and decode its parameters.  Returns null if no known adj key is found.
 */
export function parseAdjustmentFromInfo(
  additionalInfo: ReadonlyArray<{ key: string; data: Uint8Array }>,
): ParsedAdjustment | null {
  for (const block of additionalInfo) {
    const kind = ADJ_KEY_TO_KIND[block.key];
    if (kind === undefined) continue;
    const params = decodeAdjustmentParams(kind, block.data);
    return { kind, params };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Per-kind param decoders
// ---------------------------------------------------------------------------

function decodeAdjustmentParams(
  kind: AdjustmentKind,
  data: Uint8Array,
): Record<string, number | string | boolean> {
  if (data.length === 0) return {};
  try {
    switch (kind) {
      case "brightness-contrast": return decodeBrit(data);
      case "levels":              return decodeLevl(data);
      case "curves":              return decodeCurv(data);
      case "hue-saturation":      return decodeHueSat(data);
      case "color-balance":       return decodeBlnc(data);
      case "black-white":         return decodeBlwh(data);
      case "vibrance":            return decodeVibA(data);
      case "exposure":            return decodeExpA(data);
      case "channel-mixer":       return decodeMixr(data);
      case "selective-color":     return decodeSelc(data);
      case "photo-filter":        return decodePhfl(data);
      case "posterize":           return decodePost(data);
      case "threshold":           return decodeThrs(data);
      case "gradient-map":        return {}; // no simple params to decode
      case "invert":              return {};
      default:                    return {};
    }
  } catch {
    // Never let a decode failure crash import — return best-effort empty params.
    return {};
  }
}

// -- Brightness / Contrast (brit) ------------------------------------------
// Binary layout: brightness (2 bytes signed), contrast (2 bytes signed),
// optionally followed by a "use legacy" byte.
function decodeBrit(data: Uint8Array): Record<string, number> {
  if (data.length < 4) return {};
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const brightness = view.getInt16(0, false);
  const contrast = view.getInt16(2, false);
  const legacy = data.length >= 5 ? data[4] !== 0 : false;
  return { brightness, contrast, useLegacy: legacy ? 1 : 0 };
}

// -- Levels (levl) ---------------------------------------------------------
// Layout: version (2 bytes = 2), then 29 channel records × 10 bytes each.
// Each record: inputFloor u16, inputCeiling u16, outputFloor u16,
//              outputCeiling u16, gamma f32 (big-endian).
// Channel order: composite (RGB), R, G, B, A.
// We decode the composite channel (index 0) as the primary params.
function decodeLevl(data: Uint8Array): Record<string, number> {
  if (data.length < 2) return {};
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  // version is 2
  const needed = 2 + 10; // version + at least 1 record
  if (data.length < needed) return {};
  const base = 2; // skip version
  const inputBlack  = view.getUint16(base + 0, false);
  const inputWhite  = view.getUint16(base + 2, false);
  const outputBlack = view.getUint16(base + 4, false);
  const outputWhite = view.getUint16(base + 6, false);
  const gamma       = view.getFloat32(base + 8, false);
  return {
    channel: 0, // composite
    inputBlack,
    inputWhite,
    gamma: Number.isFinite(gamma) ? Math.round(gamma * 100) / 100 : 1,
    outputBlack,
    outputWhite,
  };
}

// -- Curves (curv) ---------------------------------------------------------
// Layout: version (4 bytes, = 1 or 4), count u16,
// then for each channel: curve-count u16, then curve-count × (u16, u16) pairs.
// We decode only the composite channel (index 0) to the points[] format.
function decodeCurv(data: Uint8Array): Record<string, number | string | boolean> {
  if (data.length < 6) return {};
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const version = view.getUint16(0, false);
  let offset = 0;

  // version 1: 2-byte version + 2-byte count; version 4: 4-byte version + 2-byte count
  if (version === 1) {
    offset = 2;
  } else {
    // Could be version 4 stored as u32; check: if first 2 bytes = 0 and next 2 = 4
    const v32 = view.getUint32(0, false);
    if (v32 === 4) {
      offset = 4;
    } else {
      // Unknown version layout – skip it
      return {};
    }
  }

  if (data.length < offset + 2) return {};
  const chanCount = view.getUint16(offset, false);
  offset += 2;

  // Read first channel (composite) curve points
  if (chanCount === 0 || data.length < offset + 2) return {};
  const pointCount = view.getUint16(offset, false);
  offset += 2;

  if (data.length < offset + pointCount * 4) return {};
  const points: { input: number; output: number }[] = [];
  for (let i = 0; i < pointCount; i++) {
    const inp = view.getUint16(offset, false);
    const out = view.getUint16(offset + 2, false);
    offset += 4;
    points.push({ input: inp, output: out });
  }

  return { channel: "rgb" as string, points: JSON.stringify(points) as string };
}

// -- Hue / Saturation (hue2 / hue ) ----------------------------------------
// hue2 layout (version 2): 2-byte version, then master record (3 × i16 for
// hue/sat/lig), then 6 range records.
function decodeHueSat(data: Uint8Array): Record<string, number | boolean> {
  if (data.length < 2) return {};
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const version = view.getUint16(0, false);

  let offset = 2;
  if (version === 2) {
    // hue2 block: u16 version, u16 enable-colorize, master record (3 × i16)
    if (data.length < 10) return {};
    const colorize = view.getUint16(offset, false) !== 0;
    offset += 2;
    const hue        = view.getInt16(offset, false);
    const saturation = view.getInt16(offset + 2, false);
    const lightness  = view.getInt16(offset + 4, false);
    return { hue, saturation, lightness, colorize };
  } else {
    // hue  (v1) — similar but no colorize flag at this position
    if (data.length < 8) return {};
    const hue        = view.getInt16(offset, false);
    const saturation = view.getInt16(offset + 2, false);
    const lightness  = view.getInt16(offset + 4, false);
    return { hue, saturation, lightness, colorize: false };
  }
}

// -- Color Balance (blnc) --------------------------------------------------
// Layout: 3 × 3 × i16 for (shadows, midtones, highlights) × (cyanRed,
// magentaGreen, yellowBlue), then a u8 for preserveLuminosity.
function decodeBlnc(data: Uint8Array): Record<string, number | boolean> {
  if (data.length < 18) return {};
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  return {
    shadowsCyanRed:          view.getInt16(0, false),
    shadowsMagentaGreen:     view.getInt16(2, false),
    shadowsYellowBlue:       view.getInt16(4, false),
    midtonesCyanRed:         view.getInt16(6, false),
    midtonesMagentaGreen:    view.getInt16(8, false),
    midtonesYellowBlue:      view.getInt16(10, false),
    highlightsCyanRed:       view.getInt16(12, false),
    highlightsMagentaGreen:  view.getInt16(14, false),
    highlightsYellowBlue:    view.getInt16(16, false),
    preserveLuminosity: data.length >= 19 ? data[18] !== 0 : true,
  };
}

// -- Black & White (blwh) --------------------------------------------------
// OSType descriptor containing: reds, yellows, greens, cyans, blues, magentas
// as i32, plus tintColor RGBC, tintAmount u16, useTint bool.
function decodeBlwh(data: Uint8Array): Record<string, number | boolean> {
  const d = tryReadVersionedDescriptor(data);
  if (!d) return {};
  return {
    reds:     asInteger(getItem(d, "Rd  ")) ?? 40,
    yellows:  asInteger(getItem(d, "Ylw ")) ?? 60,
    greens:   asInteger(getItem(d, "Grn ")) ?? 40,
    cyans:    asInteger(getItem(d, "Cyn ")) ?? 60,
    blues:    asInteger(getItem(d, "Bl  ")) ?? 20,
    magentas: asInteger(getItem(d, "Mgnt")) ?? 80,
  };
}

// -- Vibrance (vibA) -------------------------------------------------------
// OSType descriptor: Vbrn (vibrance i32), Strt (saturation i32).
function decodeVibA(data: Uint8Array): Record<string, number> {
  const d = tryReadVersionedDescriptor(data);
  if (!d) return {};
  return {
    vibrance:   asInteger(getItem(d, "Vbrn")) ?? 0,
    saturation: asInteger(getItem(d, "Strt")) ?? 0,
  };
}

// -- Exposure (expA) -------------------------------------------------------
// OSType descriptor: Exps (exposure), Ofst (offset), GmVl (gamma correction).
function decodeExpA(data: Uint8Array): Record<string, number> {
  const d = tryReadVersionedDescriptor(data);
  if (!d) return {};
  return {
    exposure: asDouble(getItem(d, "Exps")) ?? 0,
    offset:   asDouble(getItem(d, "Ofst")) ?? 0,
    gamma:    asDouble(getItem(d, "GmVl")) ?? 1,
  };
}

// -- Channel Mixer (mixr) --------------------------------------------------
// OSType descriptor with per-channel mixer settings.
// Key structure: Rd  (descriptor with Rd , Grn , Bl , Cst ),
//               Grn  (same), Bl  (same), Mnch (bool monochrome).
function decodeMixr(data: Uint8Array): Record<string, number | boolean> {
  const d = tryReadVersionedDescriptor(data);
  if (!d) return {};
  const out: Record<string, number | boolean> = {};
  out.monochrome = asBoolean(getItem(d, "Mnch")) ?? false;

  const channels: Array<[string, string]> = [
    ["Rd  ", "red"],
    ["Grn ", "green"],
    ["Bl  ", "blue"],
  ];
  const mixKeys: Array<[string, string]> = [
    ["Rd  ", "red"],
    ["Grn ", "green"],
    ["Bl  ", "blue"],
    ["Cst ", "constant"],
  ];
  for (const [psdChan, ourChan] of channels) {
    const chanDesc = asDescriptor(getItem(d, psdChan));
    if (chanDesc) {
      for (const [psdKey, ourKey] of mixKeys) {
        const v = asInteger(getItem(chanDesc, psdKey));
        if (v !== undefined) out[`${ourChan}.${ourKey}`] = v;
      }
    }
  }
  return out;
}

// -- Selective Color (selc) ------------------------------------------------
// OSType descriptor with per-color-range settings.
function decodeSelc(data: Uint8Array): Record<string, number | string> {
  const d = tryReadVersionedDescriptor(data);
  if (!d) return {};
  const out: Record<string, number | string> = {};

  // "Mthd" enum for relative/absolute method
  const method = asEnumerated(getItem(d, "Mthd"));
  if (method) {
    out.method = method.value === "Rltv" ? "relative" : "absolute";
  }

  const rangeMap: Record<string, string> = {
    "Rd  ": "reds",
    "Ylw ": "yellows",
    "Grn ": "greens",
    "Cyn ": "cyans",
    "Bl  ": "blues",
    "Mgnt": "magentas",
    "Wht ": "whites",
    "Ntrls": "neutrals",
    "Blcks": "blacks",
  };
  const channels: Array<[string, string]> = [
    ["Cyn ", "cyan"],
    ["Mgnt", "magenta"],
    ["Ylw ", "yellow"],
    ["Blck", "black"],
  ];
  for (const [psdRange, ourRange] of Object.entries(rangeMap)) {
    const rangeDesc = asDescriptor(getItem(d, psdRange));
    if (rangeDesc) {
      for (const [psdChan, ourChan] of channels) {
        const v = asInteger(getItem(rangeDesc, psdChan));
        if (v !== undefined) out[`${ourRange}.${ourChan}`] = v;
      }
    }
  }
  return out;
}

// -- Photo Filter (phfl / PhFl) --------------------------------------------
// OSType descriptor: Clr  (color RGBC), Dsty (density i32), Lmnc (bool).
function decodePhfl(data: Uint8Array): Record<string, number | boolean> {
  const d = tryReadVersionedDescriptor(data);
  if (!d) return {};
  const out: Record<string, number | boolean> = {};
  const colorDesc = asDescriptor(getItem(d, "Clr "));
  if (colorDesc) {
    out["color.r"] = Math.round((asDouble(getItem(colorDesc, "Rd  ")) ?? 255));
    out["color.g"] = Math.round((asDouble(getItem(colorDesc, "Grn ")) ?? 180));
    out["color.b"] = Math.round((asDouble(getItem(colorDesc, "Bl  ")) ?? 50));
  }
  const density = asInteger(getItem(d, "Dsty"));
  if (density !== undefined) out.density = density;
  const preserveLum = asBoolean(getItem(d, "Lmnc"));
  if (preserveLum !== undefined) out.preserveLuminosity = preserveLum;
  return out;
}

// -- Posterize (post) and Threshold (thrs) ---------------------------------
// Both: 2-byte level value.
function decodePost(data: Uint8Array): Record<string, number> {
  if (data.length < 2) return {};
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  return { levels: view.getUint16(0, false) };
}

function decodeThrs(data: Uint8Array): Record<string, number> {
  if (data.length < 2) return {};
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  return { level: view.getUint16(0, false) };
}

// ---------------------------------------------------------------------------
// Fill layer decoders
// ---------------------------------------------------------------------------

/**
 * Parse a SoCo (Solid Color fill) block.
 * Block format: 4-byte version, then descriptor with classId "null",
 * containing a single "Clr " key whose value is an RGBC color descriptor.
 */
export function parseSoCoDescriptor(data: Uint8Array): ParsedSolidFill | null {
  const d = tryReadVersionedDescriptor(data);
  if (!d) return null;
  const colorDesc = asDescriptor(getItem(d, "Clr "));
  if (!colorDesc) return null;
  const r = clamp8(asDouble(getItem(colorDesc, "Rd  ")) ?? 0);
  const g = clamp8(asDouble(getItem(colorDesc, "Grn ")) ?? 0);
  const b = clamp8(asDouble(getItem(colorDesc, "Bl  ")) ?? 0);
  return { color: { r, g, b, a: 1 } };
}

/**
 * Parse a GdFl (Gradient Fill) block.
 * Descriptor keys: Grad (gradient descriptor), Angl (angle), Type (enum),
 * Rvrs (reverse bool), Scl  (scale percent).
 */
export function parseGdFlDescriptor(data: Uint8Array): ParsedGradientFill | null {
  const d = tryReadVersionedDescriptor(data);
  if (!d) return null;

  const angleItem = getItem(d, "Angl");
  const angle = asDouble(angleItem) ?? 90;

  const typeItem = asEnumerated(getItem(d, "Type"));
  const gradientType = mapGradientType(typeItem?.value ?? "Lnr ");

  const reverse = asBoolean(getItem(d, "Rvrs")) ?? false;
  const scaleItem = getItem(d, "Scl ");
  const scale = asDouble(scaleItem) ?? 100;

  // Decode gradient stops from the "Grad" sub-descriptor
  const gradDesc = asDescriptor(getItem(d, "Grad"));
  const stops = gradDesc ? decodeGradientStops(gradDesc) : defaultStops();

  return { gradientType, angle, scale, reverse, stops };
}

function mapGradientType(psdType: string): ParsedGradientFill["gradientType"] {
  switch (psdType) {
    case "Lnr ": return "linear";
    case "Rdl ": return "radial";
    case "Angl": return "angle";
    case "Rflc": return "reflected";
    case "Dmnd": return "diamond";
    default:     return "linear";
  }
}

function defaultStops(): GradientStop[] {
  return [
    { position: 0, color: { r: 0, g: 0, b: 0, a: 1 }, midpoint: 0.5 },
    { position: 1, color: { r: 255, g: 255, b: 255, a: 1 }, midpoint: 0.5 },
  ];
}

function decodeGradientStops(
  gradDesc: OSTypeValue & { kind: "descriptor" },
): GradientStop[] {
  const colorStopsList = asList(getItem(gradDesc, "Clrs"));
  if (!colorStopsList || colorStopsList.length === 0) return defaultStops();

  const stops: GradientStop[] = [];
  for (const stopVal of colorStopsList) {
    const stopDesc = asDescriptor(stopVal);
    if (!stopDesc) continue;
    const colorDesc = asDescriptor(getItem(stopDesc, "Clr "));
    if (!colorDesc) continue;
    const r = clamp8(asDouble(getItem(colorDesc, "Rd  ")) ?? 0);
    const g = clamp8(asDouble(getItem(colorDesc, "Grn ")) ?? 0);
    const b = clamp8(asDouble(getItem(colorDesc, "Bl  ")) ?? 0);
    const posItem = getItem(stopDesc, "Lctn");
    // Position is stored as 0..4096, normalize to 0..1
    const posRaw = asDouble(posItem) ?? 0;
    const position = posRaw / 4096;
    const midRaw = asDouble(getItem(stopDesc, "Mdpn")) ?? 2048;
    const midpoint = midRaw / 4096;
    stops.push({ position, color: { r, g, b, a: 1 }, midpoint });
  }
  return stops.length >= 2 ? stops : defaultStops();
}

/**
 * Parse a PtFl (Pattern Fill) block.
 * Descriptor keys: Ptrn (pattern name string), Scl  (scale percent).
 */
export function parsePtFlDescriptor(data: Uint8Array): ParsedPatternFill | null {
  const d = tryReadVersionedDescriptor(data);
  if (!d) return null;

  const ptrnDesc = asDescriptor(getItem(d, "Ptrn"));
  const patternName = ptrnDesc
    ? (asString(getItem(ptrnDesc, "Nm  ")) ?? "")
    : "";
  const scaleItem = getItem(d, "Scl ");
  const scale = asDouble(scaleItem) ?? 100;

  return { patternName, scale };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Try to read a versioned OSType descriptor from raw bytes; return null on any error. */
function tryReadVersionedDescriptor(
  data: Uint8Array,
): (OSTypeValue & { kind: "descriptor" }) | null {
  if (!data || data.length < 4) return null;
  try {
    const r = new BinaryReader(data);
    const { descriptor } = readVersionedDescriptor(r);
    return descriptor;
  } catch (e) {
    if (e instanceof BinaryReaderError || e instanceof OSTypeDescriptorError) {
      return null;
    }
    throw e;
  }
}

function clamp8(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return n < 0 ? 0 : n > 255 ? 255 : Math.round(n);
}
